// @ts-nocheck
/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Plus, ArrowRightLeft } from 'lucide-react';
import useAppStore from '../store/index';
import { useCalculationsSelector } from '../store/selectors/calculations';
import { currencyLabel } from '../utils/currencyFormatter';

/* --- GLOBAL CONSTANTS --- */
const WARENWIRTSCHAFTSFAKTOR = 1.05;

/* --- HELPER FUNCTIONS --- */
const parseNumber = (str: any): number => {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  return parseFloat(String(str).replace(/\./g, '').replace(',', '.').replace(/[^0-9.-]/g, ''));
};

const formatNumber = (num: number, fractionDigits = 2): string => {
  if (isNaN(num)) num = 0;
  return num.toLocaleString('de-DE', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
};

// --- KOMPONENTE: SMARTER TABLE INPUT ---
const TableInput: React.FC<{ 
    value: string | number; 
    onChange: (val: string) => void; 
    onBlur: (val: string) => void;
    align?: 'left' | 'center';
    className?: string;
}> = ({ value, onChange, onBlur, align = 'center', className = '' }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    return (
        <input 
            type="text"
            className={`bg-transparent text-pe-text font-bold font-mono w-full border-none outline-none text-${align} p-0 m-0 text-sm h-full ${className}`}
            value={localValue}
            onChange={(e) => {
                setLocalValue(e.target.value);
                onChange(e.target.value);
            }}
            onBlur={(e) => onBlur(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
        />
    );
};

const ProduktRechnerPage: React.FC = () => {
  // --- STORE CONNECTION ---
  const { clientData, settings, updateNestedClientData } = useAppStore();
  const storeCalc = useCalculationsSelector();
  
  const safeSettings = settings as any; 
  const safeClientData = clientData as any;

  // 1. Währung & MwSt
  const currencySymbol = currencyLabel(
      safeSettings?.country?.currency || storeCalc?.currencyCode,
      safeClientData?.land || safeSettings?.selectedCountryId
  );
  const mwstSatz = useMemo(() => {
      if (storeCalc?.mwstSatz !== undefined && storeCalc?.mwstSatz !== null) {
          return Number(storeCalc.mwstSatz);
      }
      if (safeClientData?.isVatExempt) return 0;
      return safeSettings?.country?.MwSt_Satz ?? 19;
  }, [storeCalc?.mwstSatz, safeClientData?.isVatExempt, safeSettings?.country?.MwSt_Satz]);
  const mwstFaktor = 1 + (mwstSatz / 100);

  // 2. Datenzugriff
  const produktData = safeClientData?.produktRechnerData || {
      zielGewinnVk: '15,24',
      zielUeberschuss: '3,56',
      zielBedienzeit: '60',
      produkte: []
  };

  // --- EXAKTE KALKULATOR-LOGIK (REPLIZIERT) ---
  const kalkulatorWerte = useMemo(() => {
      const inputs = safeClientData?.kalkulator?.inputs || {};
      const kalkState = safeClientData?.kalkulator || {};

      // 1. Inputs holen
      const neuerPreis = parseNumber(inputs.neuerPreis);
      const bedienzeit = parseNumber(inputs.bedienzeit);
      const materialPreisInput = parseNumber(inputs.materialPreis);
      const materialGramm = parseNumber(inputs.materialGramm);

      // 2. Faktoren aus dem Rechenkern (Selector)
      const mwst = (storeCalc.mwstSatz || 0) / 100;
      const gk = (storeCalc.gemeinkostenfaktor || 0); // Gemeinkostenfaktor
      // Kanonisch: Anteil am Netto-Monatsumsatz (wie Selektor unprodUnternehmerlohnPercent)
      const ul = (storeCalc.unprodUnternehmerlohnPercent || 0) / 100;
      const vl = (safeSettings?.global?.Verlust_1 || 0) / 100; // Kalk. Verlust

      // 3. Materialkosten
      const activeMatIds = kalkState.activeMaterialIds || [];
      const materialListe = safeSettings?.materialListe || [];
      let mSum = 0; let mCnt = 0;
      activeMatIds.forEach((id: string) => {
          const m = materialListe.find((x: any) => x.id === id);
          if(m && m.gebinde > 0) { mSum += (m.ekPreis/m.gebinde)*100; mCnt++; }
      });
      const avg100 = mCnt > 0 ? mSum/mCnt : 0;
      // Produktrechner = Warenaufschlag DL (Default 1.05). Kalkulator nutzt separat VK 1.30.
      const wawiDl = Number(safeSettings?.global?.Warenwirtschaftsaufschlag_DL);
      const matFaktor = Number.isFinite(wawiDl) && wawiDl > 0 ? wawiDl : 1.05;
      const matCost = ((avg100/100)*materialGramm + materialPreisInput) * matFaktor;

      // 4. Netto Umsatz
      const mwstVal = neuerPreis - (neuerPreis/(1+mwst));
      const netto = neuerPreis - mwstVal;

      // 5. Gerätekosten
      const activeDevIds = kalkState.activeDeviceIds || [];
      const geraeteListe = safeSettings?.global?.geraeteListe || [];
      let dPerc = 0;
      activeDevIds.forEach((id: string) => {
          const d = geraeteListe.find((x: any) => x?.id === id);
          if(d) dPerc += d.kostenProzent;
      });
      const devCostTotal = netto * (dPerc / 100);

      // 6. Zuschläge
      const surchargeId = kalkState.selectedSurchargeId;
      const surchargeList = safeSettings?.surchargeListe || [];
      const sPrice = (surchargeList.find((x: any) => x.id === surchargeId)?.price || 0) / (1 + mwst);

      // 7. "Es bleibt übrig für Lohn"
      const uebrig = netto - matCost - devCostTotal - (netto*gk) - (netto*ul) - (netto*vl) + sPrice;

      // 8. Lohnkosten (3-Stufen-Modell)
      const teamStundenlohn = storeCalc.teamStundenlohn || 0;
      const lohnBasis = teamStundenlohn * (bedienzeit / 60);
      
      const lohnfortzahlungPercent = safeSettings?.global?.Urlaubsausgleich || 20;
      const urlaubVal = lohnBasis * (lohnfortzahlungPercent / 100);

      const avgSozialPercent = storeCalc.avgSozialabgabenPercent || 0;
      const sozialVal = (lohnBasis + urlaubVal) * (avgSozialPercent / 100);

      const totalLohn = lohnBasis + urlaubVal + sozialVal;

      // 9. FINALER GEWINN
      const gewinn = uebrig - totalLohn;

      return {
          ueberschuss: gewinn,
          zeit: bedienzeit
      };

  }, [safeClientData, safeSettings, storeCalc]);


  // --- STATE: LOCAL CALCULATOR ---
  const [vatMode, setVatMode] = useState<'bn' | 'nb'>('bn'); 
  const [vatInput, setVatInput] = useState<string>('36,50');
  const [vatResult, setVatResult] = useState<string>('30,67');
  const [calcMode, setCalcMode] = useState<'target_vk' | 'target_marge'>('target_vk');
  const [calcEk, setCalcEk] = useState<string>('80,00');
  const [calcMarge, setCalcMarge] = useState<string>('100,0');
  const [calcVk, setCalcVk] = useState<string>('160,00');
  const [calcResultVk, setCalcResultVk] = useState<string>(''); 
  const [calcResultMarge, setCalcResultMarge] = useState<string>(''); 

  // --- STORE ACTIONS ---
  const updateZielAnalyse = (field: string, value: string) => {
      updateNestedClientData('produktRechnerData', field, value);
  };

  const updateProduktTabelle = (newProdukte: any[]) => {
      updateNestedClientData('produktRechnerData', 'produkte', newProdukte);
  };

  const addRow = () => {
      const newRow = { id: Date.now().toString(), name: '', ek: 0, vk: 0 };
      updateProduktTabelle([...(produktData.produkte || []), newRow]);
  };

  const removeRow = (id: string) => {
      const filtered = (produktData.produkte || []).filter((p: any) => p.id !== id);
      updateProduktTabelle(filtered);
  };

  const updateProduct = (id: string, field: 'name' | 'ek' | 'vk', value: string) => {
      const updated = (produktData.produkte || []).map((p: any) => {
          if (p.id === id) {
              return { 
                  ...p, 
                  [field]: field === 'name' ? value : parseNumber(value) 
              };
          }
          return p;
      });
      updateProduktTabelle(updated);
  };

  const handleImportCache = () => {
      const ueberschussStr = formatNumber(kalkulatorWerte.ueberschuss);
      const zeitStr = formatNumber(kalkulatorWerte.zeit, 0); 

      updateZielAnalyse('zielUeberschuss', ueberschussStr);
      updateZielAnalyse('zielBedienzeit', zeitStr);
  };

  // --- EFFECTS: ZIEL ANALYSE ---
  const berechneZielErgebnis = () => {
      const gewinn = parseNumber(produktData.zielGewinnVk);
      const ueberschuss = parseNumber(produktData.zielUeberschuss);
      const zeit = parseNumber(produktData.zielBedienzeit);

      if (ueberschuss > 0) {
          const faktor = gewinn / ueberschuss;
          const stunden = (faktor * zeit) / 60;
          return {
              faktor: formatNumber(faktor, 1),
              stunden: formatNumber(stunden, 1)
          };
      }
      return { faktor: '–', stunden: '–' };
  };
  const zielErgebnis = berechneZielErgebnis();

  // --- EFFECTS: LOCAL CALCULATORS ---
  useEffect(() => {
    const val = parseNumber(vatInput);
    const res = vatMode === 'bn' ? val / mwstFaktor : val * mwstFaktor;
    setVatResult(formatNumber(res));
  }, [vatInput, vatMode, mwstFaktor]);

  useEffect(() => {
    const ek = parseNumber(calcEk);
    if (calcMode === 'target_vk') {
      const marge = parseNumber(calcMarge);
      const calculatedVk = ek * (1 + marge / 100);
      setCalcResultVk(formatNumber(calculatedVk));
    } else {
      const vk = parseNumber(calcVk);
      let calculatedMarge = 0;
      if (ek > 0) calculatedMarge = ((vk / ek) - 1) * 100;
      setCalcResultMarge(formatNumber(calculatedMarge, 1));
    }
  }, [calcEk, calcMarge, calcVk, calcMode]);

  // --- STYLES ---
  const customStyles = `
    @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

    .font-sans { font-family: 'Inter', sans-serif; }
    .font-mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

    .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #030303; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #1a1a1a; border-radius: 5px; border: 2px solid #030303; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #333; }

    /* Carbon Weave Design */
    .carbon-weave-bg {
        background-color: #111;
        background-image:
            radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
            radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
            radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
            radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
            radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
        background-size: 20px 20px;
    }

    .carbon-panel {
        position: relative;
        border-radius: 1.5rem;
        border: 1px solid #1a1a1a;
        box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
        overflow: hidden;
        z-index: 1;
    }

    .carbon-panel.ziel-analyse-panel,
    .carbon-panel.produkt-rechner-main-panel {
        overflow: visible;
    }

    .carbon-panel.ziel-analyse-panel .carbon-content,
    .carbon-panel.produkt-rechner-main-panel .carbon-content {
        overflow: visible;
    }

    .carbon-panel::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.55));
        pointer-events: none;
        z-index: 1;
    }

    .carbon-content {
        position: relative;
        z-index: 10;
    }

    /* Toggle Switch Styling */
    .toggle-container { background-color: #000; border: 1px solid #333; border-radius: 9999px; display: inline-flex; padding: 4px; align-items: center; }
    .toggle-pill { padding: 6px 16px; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; border-radius: 9999px; cursor: pointer; transition: all 0.2s ease; white-space: nowrap; }
    .toggle-pill-active { background-color: #E6C299; color: #000; box-shadow: 0 2px 10px rgba(230,194,153,0.3); }
    .toggle-pill-inactive { color: #64748b; }
    .toggle-pill-inactive:hover { color: white; }
    .toggle-arrow { color: #E6C299; font-size: 0.8rem; padding: 0 8px; opacity: 0.5; }

    /* TOOLTIP STYLES */
    [data-tooltip-text] { position: relative; }
    [data-tooltip-text]:hover::after {
        content: attr(data-tooltip-text);
        position: absolute;
        bottom: 100%; left: 50%; transform: translateX(-50%);
        margin-bottom: 12px;
        background-color: #E6C299; color: #000000; border: 1px solid #000000;
        font-weight: 800; font-family: 'Inter', sans-serif;
        padding: 10px 14px; border-radius: 8px;
        white-space: normal; z-index: 9999; font-size: 0.75rem;
        box-shadow: 0 10px 25px rgba(0,0,0,0.8); width: max-content;
        max-width: 250px; text-align: center; pointer-events: none;
    }

    .ziel-analyse-panel [data-tooltip-text]:hover::after {
        z-index: 99999;
    }

    .input-wrapper { background-color: #000; border: 1px solid #333; transition: all 0.2s; }
    .input-wrapper:hover { border-color: #E6C299; }
    .input-wrapper:focus-within { border-color: #E6C299; box-shadow: 0 0 0 1px #E6C299; }
    
    .result-wrapper { background-color: #0a0a0a; border: 1px solid #222; box-shadow: inset 0 2px 10px rgba(0,0,0,0.5); }

    /* Reingewinn: Schlüsselkennzahl — klar hervorgehoben (hell + dunkel) */
    .result-wrapper.result-reingewinn {
        background: linear-gradient(180deg, rgba(230,194,153,0.18) 0%, rgba(230,194,153,0.08) 100%);
        border: 1.5px solid rgba(230,194,153,0.55);
        box-shadow: inset 0 1px 0 rgba(230,194,153,0.15), 0 0 18px rgba(230,194,153,0.12);
    }
    .result-wrapper.result-reingewinn input {
        color: #E6C299;
        font-size: 0.95rem;
        font-weight: 800;
        letter-spacing: 0.01em;
    }
    .result-wrapper.result-reingewinn .result-suffix {
        color: #E6C299;
        opacity: 0.85;
    }

    /* ===== LIGHT-MODE OVERRIDES (Warm-Platin) — Dark bleibt via html.dark unangetastet ===== */
    html:not(.dark) .custom-scrollbar::-webkit-scrollbar-track { background: var(--pe-bg-primary); }
    html:not(.dark) .custom-scrollbar::-webkit-scrollbar-thumb { background: #C8C2BB; border: 2px solid var(--pe-bg-primary); }
    html:not(.dark) .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #B8B2AC; }
    html:not(.dark) .carbon-weave-bg {
        background-color: var(--pe-bg-elevated);
        background-image: none;
    }
    html:not(.dark) .carbon-panel {
        border-color: var(--pe-border-subtle);
        box-shadow: 0 20px 45px -20px rgba(28, 25, 23, 0.18), 0 2px 8px rgba(28, 25, 23, 0.06);
    }
    html:not(.dark) .carbon-panel::after { background: none; }
    html:not(.dark) .toggle-container { background-color: var(--pe-bg-input); border-color: var(--pe-border-strong); }
    html:not(.dark) .toggle-pill-inactive { color: #78716C; }
    html:not(.dark) .toggle-pill-inactive:hover { color: var(--pe-text-primary); }
    html:not(.dark) .input-wrapper { background-color: var(--pe-bg-input); border-color: var(--pe-border-strong); }
    html:not(.dark) .result-wrapper { background-color: var(--pe-bg-tile-header); border-color: var(--pe-border-subtle); box-shadow: inset 0 1px 3px rgba(28,25,23,0.08); }
    html:not(.dark) .result-wrapper.result-reingewinn {
        background: linear-gradient(180deg, #FFF8EF 0%, #F5EDE0 100%);
        border: 1.5px solid #C4A574;
        box-shadow: 0 1px 0 rgba(141,100,53,0.08), 0 2px 8px rgba(141,100,53,0.10);
    }
    html:not(.dark) .result-wrapper.result-reingewinn input {
        color: #6B4A22;
    }
    html:not(.dark) .result-wrapper.result-reingewinn .result-suffix {
        color: #8D6435;
        opacity: 0.9;
    }
  `;

  return (
    <div className="w-full min-h-screen bg-pe-bg dark:bg-[#030303] text-pe-muted dark:text-gray-300 font-sans pb-32">
      <style>{customStyles}</style>

      <main className="w-full mx-auto max-w-[1920px] p-2 sm:p-6 lg:p-8">
        
        <div className="flex flex-col xl:flex-row gap-8 items-start">
            
            {/* LINKER BEREICH: TABELLE */}
            <section className="w-full flex-grow xl:w-auto carbon-panel carbon-weave-bg shadow-2xl produkt-rechner-main-panel">
                 <div className="carbon-content">
                     <div className="carbon-weave-bg p-6 border-b border-pe-border dark:border-[#222]">
                         <h2 className="text-xl font-bold text-pe-accent-text uppercase tracking-widest">Produkte & Margen (MwSt: {formatNumber(mwstSatz, 1)}%)</h2>
                    </div>
                    
                    <div className="overflow-x-auto overflow-y-visible custom-scrollbar bg-pe-tile-header">
                        <table className="w-full border-collapse align-middle" style={{ minWidth: '950px' }}>
                            <thead>
                                <tr className="bg-pe-card border-b border-pe-border dark:border-[#222] text-xs font-semibold text-pe-muted uppercase tracking-wider">
                                    <th className="p-4 text-left w-auto min-w-[200px]">Produkt</th>
                                    <th className="p-4 text-center w-[130px]">EK-Preis (Netto)</th>
                                    <th className="p-4 text-center w-[130px]">UVP/VK-Preis (Brutto)</th>
                                    <th className="p-4 text-center w-[130px] leading-tight">
                                        Verkaufs<br />marge
                                    </th>
                                    <th className="p-4 text-center w-[130px] text-[#8D6435] dark:text-[#E6C299]">Reingewinn</th>
                                    <th className="p-4 text-center w-[130px] leading-tight text-emerald-800 dark:text-emerald-400">
                                        Verkaufs<br />gewinn
                                    </th>
                                    <th className="p-4 text-center w-[40px]"></th> 
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#1a1a1a]">
                                {(produktData.produkte || []).map((product: any) => {
                                    const margeProzent = product.ek > 0 ? ((product.vk / product.ek) - 1) * 100 : 0;
                                    const vkNetto = product.vk / mwstFaktor; 
                                    const wareneinsatz = product.ek * WARENWIRTSCHAFTSFAKTOR; 
                                    const reingewinn = vkNetto - wareneinsatz;
                                    const gewinnProzent = wareneinsatz > 0 ? (reingewinn / wareneinsatz) * 100 : 0;

                                    return (
                                      <tr key={product.id} className="hover:bg-white/5 transition-colors">
                                          {/* NAME */}
                                          <td className="p-3">
                                              <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 input-wrapper" data-tooltip-text="Produktname eingeben.">
                                                  <TableInput 
                                                    value={product.name}
                                                    onChange={(val) => {}} 
                                                    onBlur={(val) => updateProduct(product.id, 'name', val)}
                                                    align="left"
                                                  />
                                              </div>
                                          </td>
                                          
                                          {/* EK */}
                                          <td className="p-3">
                                              <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 input-wrapper" data-tooltip-text="EK-Preis (Netto): Netto-Einkaufspreis laut Hersteller.">
                                                  <TableInput 
                                                    value={formatNumber(product.ek)}
                                                    onChange={(val) => {}} 
                                                    onBlur={(val) => updateProduct(product.id, 'ek', val)}
                                                    className="pr-8"
                                                  />
                                                  <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                                                      <span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span>
                                                  </div>
                                              </div>
                                          </td>

                                          {/* VK */}
                                          <td className="p-3">
                                              <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 input-wrapper" data-tooltip-text="UVP/VK-Preis (Brutto): Verkaufspreis inkl. MwSt (UVP oder selbst bestimmt).">
                                                  <TableInput 
                                                    value={formatNumber(product.vk)}
                                                    onChange={(val) => {}} 
                                                    onBlur={(val) => updateProduct(product.id, 'vk', val)}
                                                    className="pr-8"
                                                  />
                                                  <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                                                      <span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span>
                                                  </div>
                                              </div>
                                          </td>

                                          {/* MARGE */}
                                          <td className="p-3">
                                              <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 result-wrapper" data-tooltip-text="Verkaufsmarge: Prozentualer Aufschlag auf den Einkaufspreis.">
                                                  <input 
                                                    type="text" 
                                                    className="bg-transparent text-pe-accent-text font-mono font-bold w-full border-none outline-none text-center p-0 m-0 text-sm h-full pr-8 cursor-default"
                                                    readOnly 
                                                    value={formatNumber(margeProzent, 1)}
                                                  />
                                                  <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                                                      <span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">%</span>
                                                  </div>
                                              </div>
                                          </td>

                                          {/* REINGEWINN — hervorgehobene Schlüsselkennzahl */}
                                          <td className="p-3">
                                              <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 result-wrapper result-reingewinn" data-tooltip-text="Ertrag nach Abzug aller Kosten (Netto).">
                                                  <input 
                                                    type="text" 
                                                    className="bg-transparent font-mono w-full border-none outline-none text-center p-0 m-0 h-full pr-8 cursor-default"
                                                    readOnly 
                                                    value={formatNumber(reingewinn, 2)}
                                                  />
                                                  <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                                                      <span className="result-suffix font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span>
                                                  </div>
                                              </div>
                                          </td>

                                          {/* GEWINN % */}
                                          <td className="p-3">
                                              <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 result-wrapper" data-tooltip-text="Verkaufsgewinn: Reingewinn in Prozent vom Wareneinsatz.">
                                                  <input 
                                                    type="text" 
                                                    className="bg-transparent text-emerald-800 dark:text-emerald-400 font-mono font-bold w-full border-none outline-none text-center p-0 m-0 text-sm h-full pr-8 cursor-default"
                                                    readOnly 
                                                    value={formatNumber(gewinnProzent, 1)}
                                                  />
                                                  <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none">
                                                      <span className="text-emerald-700/70 dark:text-emerald-400/60 font-bold text-[0.7rem] tracking-widest">%</span>
                                                  </div>
                                              </div>
                                          </td>

                                          {/* DELETE */}
                                          <td className="p-2 text-center">
                                              <div className="w-full flex justify-center">
                                                  <button 
                                                    onClick={() => removeRow(product.id)}
                                                    className="text-gray-600 p-1.5 rounded-lg hover:text-red-500 hover:bg-red-500/10 transition-colors flex items-center justify-center"
                                                    title="Zeile löschen"
                                                  >
                                                      <Trash2 size={18} />
                                                  </button>
                                              </div>
                                          </td>
                                      </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                
                        <div className="p-6 border-t border-pe-border dark:border-[#222] bg-pe-sunken">
                            <button 
                              onClick={addRow}
                              className="bg-pe-card hover:bg-white hover:text-black text-pe-accent-text px-6 py-3 rounded-xl border border-pe-border-strong transition-all whitespace-nowrap font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 w-auto shadow-[0_0_15px_rgba(0,0,0,0.5)]"
                            >
                                <Plus size={16} /> Produktzeile hinzufügen
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* RECHTER BEREICH: TOOLS — scrollt mit der Seite (kein sticky) */}
            <aside className="w-full xl:w-[380px] flex-shrink-0 space-y-8 pb-10">
                
                {/* 1. ZIEL-ANALYSE */}
                <div className="carbon-panel carbon-weave-bg shadow-2xl ziel-analyse-panel">
                    <div className="carbon-content flex flex-col h-full">
                        <div className="carbon-weave-bg px-6 py-4 border-b border-pe-border dark:border-[#222]">
                            <h2 className="text-sm font-bold text-pe-accent-text text-center uppercase tracking-widest">Ziel-Analyse</h2>
                        </div>
                        
                        <div className="p-6">
                            <div className="grid grid-cols-3 gap-4 mb-8 items-end">
                                <div>
                                    <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">Gewinn (Ware)</div>
                                    <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 input-wrapper" data-tooltip-text="Gewinn aus dem Verkaufsprodukt (siehe Tabelle oben: Reingewinn).">
                                        <TableInput 
                                          value={produktData.zielGewinnVk}
                                          onChange={(val) => {}}
                                          onBlur={(val) => updateZielAnalyse('zielGewinnVk', formatNumber(parseNumber(val)))}
                                          className="pr-8"
                                        />
                                        <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[10px] uppercase tracking-widest">{currencySymbol}</span></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">Überschuss (DL)</div>
                                    <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 input-wrapper" data-tooltip-text="Dein kalkulatorischer Überschuss pro Dienstleistung (aus DL-Kalkulator).">
                                        <TableInput 
                                          value={produktData.zielUeberschuss}
                                          onChange={(val) => {}}
                                          onBlur={(val) => updateZielAnalyse('zielUeberschuss', formatNumber(parseNumber(val)))}
                                          className="pr-8"
                                        />
                                        <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[10px] uppercase tracking-widest">{currencySymbol}</span></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">Zeit (DL)</div>
                                    <div className="relative flex items-center rounded-lg w-full h-[46px] px-2 input-wrapper" data-tooltip-text="Benötigte Zeit für die Dienstleistung in Minuten.">
                                        <TableInput 
                                          value={produktData.zielBedienzeit}
                                          onChange={(val) => {}}
                                          onBlur={(val) => updateZielAnalyse('zielBedienzeit', formatNumber(parseNumber(val)))}
                                          className="pr-8"
                                        />
                                        <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[10px] uppercase tracking-widest">min</span></div>
                                    </div>
                                </div>
                            </div>

                            {/* Ergebnis Box */}
                            <div className="bg-pe-sunken border border-pe-border dark:border-[#222] rounded-xl p-6 flex justify-around items-center text-center shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                                <div data-tooltip-text="So oft musst du die DL durchführen, um denselben Gewinn zu erzielen wie beim Produktverkauf.">
                                    <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider pb-2">Faktor</div>
                                    <div className="text-3xl font-mono font-black text-pe-accent-text drop-shadow-md">{zielErgebnis.faktor}</div>
                                    <div className="text-[10px] text-gray-600 uppercase font-bold mt-2 tracking-widest">Mal Erarbeiten</div>
                                </div>
                                <div className="h-16 w-px bg-[#222]"></div>
                                <div data-tooltip-text="So lange musst du arbeiten, um diesen Gewinn rein über die Dienstleistung zu erwirtschaften.">
                                    <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider pb-2">Aufwand</div>
                                    <div className="text-3xl font-mono font-black text-pe-accent-text drop-shadow-md">{zielErgebnis.stunden}</div>
                                    <div className="text-[10px] text-gray-600 uppercase font-bold mt-2 tracking-widest">Stunden</div>
                                </div>
                            </div>

                            {/* Import Button */}
                            <div className="mt-8 pt-6 border-t border-pe-border dark:border-[#222]">
                                <div className="flex justify-between items-center mb-3 px-1">
                                    <span className="text-xs font-semibold text-pe-muted uppercase font-bold tracking-wider">Import (Kalkulator):</span>
                                    <div className="text-sm font-mono font-bold text-pe-muted dark:text-gray-300">
                                         <span title="Dienstleistungs-Überschuss">{formatNumber(kalkulatorWerte.ueberschuss)} {currencySymbol}</span>
                                         <span className="mx-2 text-gray-700">|</span> 
                                         <span title="Dauer">{formatNumber(kalkulatorWerte.zeit, 0)} min</span>
                                    </div>
                                </div>
                                <button 
                                  onClick={handleImportCache}
                                  className="w-full bg-pe-card hover:bg-white hover:text-black text-pe-accent-text border border-pe-border-strong font-black py-4 rounded-xl text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-3 shadow-[0_0_15px_rgba(0,0,0,0.5)]" 
                                  data-tooltip-text="Kopiert die aktuell kalkulierten Werte aus dem Kalkulator in die Felder der Ziel-Analyse."
                                >
                                    <span>↓</span> Werte übernehmen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. MARGIN CALCULATOR & VAT TOOLS */}
                <div className="carbon-panel carbon-weave-bg shadow-2xl h-full flex flex-col">
                    <div className="carbon-content flex flex-col h-full">
                        <div className="carbon-weave-bg px-6 py-4 border-b border-pe-border dark:border-[#222]">
                            <h2 className="text-sm font-bold text-pe-accent-text text-center uppercase tracking-widest">Kalkulator Tools ({formatNumber(mwstSatz, 1)}% MwSt)</h2>
                        </div>
                        
                        <div className="p-6 space-y-8 flex-grow text-pe-text">
                            
                            {/* PART 1: BRUTTO/NETTO */}
                            <div data-tooltip-text={`Schnelle Umrechnung von Beträgen inklusive oder exklusive ${formatNumber(mwstSatz, 1)}% Mehrwertsteuer.`}>
                                <div className="flex justify-center mb-4">
                                    <div 
                                      className="bg-pe-input dark:bg-black border border-pe-border-strong rounded-full inline-flex p-[2px] items-center cursor-pointer select-none transition-colors toggle-container shadow-lg" 
                                      onClick={() => setVatMode(vatMode === 'bn' ? 'nb' : 'bn')}
                                    >
                                        <div className={`px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.05em] rounded-full transition-all toggle-pill ${vatMode === 'bn' ? 'bg-[#E6C299] text-[#0f172a] shadow-sm' : 'text-[#64748b]'}`}>Brutto</div>
                                        <div className="text-pe-accent-text text-[0.8rem] px-1"><ArrowRightLeft size={14} /></div>
                                        <div className={`px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.05em] rounded-full transition-all toggle-pill ${vatMode === 'nb' ? 'bg-[#E6C299] text-[#0f172a] shadow-sm' : 'text-[#64748b]'}`}>Netto</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 items-end bg-pe-tile-header p-4 rounded-xl border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                                    <div>
                                        <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">
                                          {vatMode === 'bn' ? 'Eingabe (Brutto)' : 'Eingabe (Netto)'}
                                        </div>
                                        <div className="relative flex items-center border border-pe-border-strong bg-pe-input dark:bg-black rounded-lg w-full h-[42px] px-2 input-wrapper">
                                            <TableInput 
                                              value={vatInput}
                                              onChange={setVatInput}
                                              onBlur={() => setVatInput(formatNumber(parseNumber(vatInput)))}
                                              className="pr-8"
                                            />
                                            <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span></div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">
                                          {vatMode === 'bn' ? 'Ergebnis (Netto)' : 'Ergebnis (Brutto)'}
                                        </div>
                                        <div className="bg-pe-card border border-pe-border dark:border-[#222] rounded-lg p-2 text-center text-pe-accent-text font-bold font-mono shadow-inner h-[42px] flex items-center justify-center">
                                            {vatResult} <span className="text-[0.7rem] ml-1 text-stone-500 dark:text-gray-500">{currencySymbol}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <hr className="border-[#222]" />

                            {/* PART 2: MARGE/VK */}
                            <div data-tooltip-text="Ermitteln Sie den optimalen Verkaufspreis oder prüfen Sie die Marge bei einem festen Preis.">
                                <div className="flex justify-center mb-4">
                                    <div 
                                      className="bg-pe-input dark:bg-black border border-pe-border-strong rounded-full inline-flex p-[2px] items-center cursor-pointer select-none transition-colors toggle-container shadow-lg" 
                                      onClick={() => setCalcMode(calcMode === 'target_vk' ? 'target_marge' : 'target_vk')}
                                    >
                                        <div className={`px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.05em] rounded-full transition-all toggle-pill ${calcMode === 'target_vk' ? 'bg-[#E6C299] text-[#0f172a] shadow-sm' : 'text-[#64748b]'}`}>VK-Preis</div>
                                        <div className="text-pe-accent-text text-[0.8rem] px-1"><ArrowRightLeft size={14} /></div>
                                        <div className={`px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.05em] rounded-full transition-all toggle-pill ${calcMode === 'target_marge' ? 'bg-[#E6C299] text-[#0f172a] shadow-sm' : 'text-[#64748b]'}`}>Marge</div>
                                    </div>
                                </div>

                                <div className="space-y-4 bg-pe-tile-header p-4 rounded-xl border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                                    {calcMode === 'target_vk' ? (
                                      <>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">EK Preis</div>
                                                <div className="relative flex items-center border border-pe-border-strong bg-pe-input dark:bg-black rounded-lg w-full h-[42px] px-2 input-wrapper">
                                                    <TableInput 
                                                      value={calcEk}
                                                      onChange={setCalcEk}
                                                      onBlur={() => setCalcEk(formatNumber(parseNumber(calcEk)))}
                                                      className="pr-8"
                                                    />
                                                    <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span></div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">Marge %</div>
                                                <div className="relative flex items-center border border-pe-border-strong bg-pe-input dark:bg-black rounded-lg w-full h-[42px] px-2 input-wrapper">
                                                    <TableInput 
                                                      value={calcMarge}
                                                      onChange={setCalcMarge}
                                                      onBlur={() => setCalcMarge(formatNumber(parseNumber(calcMarge), 1))}
                                                      className="pr-6"
                                                    />
                                                    <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">%</span></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="pt-3 border-t border-[#1a1a1a]">
                                            <div className="text-pe-accent-text text-[10px] uppercase tracking-widest font-bold text-center pb-2">Verkaufspreis (Brutto)</div>
                                            <div className="bg-pe-card border border-customBeige/30 rounded-lg p-3 text-center text-pe-accent-text text-xl font-bold font-mono shadow-[0_0_15px_rgba(230,194,153,0.1)] h-[50px] flex items-center justify-center">
                                                {calcResultVk} <span className="text-[0.8rem] ml-1.5 text-stone-500 dark:text-gray-500 font-sans">{currencySymbol}</span>
                                            </div>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">EK Preis</div>
                                                <div className="relative flex items-center border border-pe-border-strong bg-pe-input dark:bg-black rounded-lg w-full h-[42px] px-2 input-wrapper">
                                                    <TableInput 
                                                      value={calcEk}
                                                      onChange={setCalcEk}
                                                      onBlur={() => setCalcEk(formatNumber(parseNumber(calcEk)))}
                                                      className="pr-8"
                                                    />
                                                    <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span></div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">VK Preis</div>
                                                <div className="relative flex items-center border border-pe-border-strong bg-pe-input dark:bg-black rounded-lg w-full h-[42px] px-2 input-wrapper">
                                                    <TableInput 
                                                      value={calcVk}
                                                      onChange={setCalcVk}
                                                      onBlur={() => setCalcVk(formatNumber(parseNumber(calcVk)))}
                                                      className="pr-8"
                                                    />
                                                    <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none"><span className="text-stone-500 dark:text-gray-500 font-bold text-[0.7rem] tracking-widest">{currencySymbol}</span></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="pt-3 border-t border-[#1a1a1a]">
                                            <div className="text-pe-accent-text text-[10px] uppercase tracking-widest font-bold text-center pb-2">Erzielte Marge</div>
                                            <div className="bg-pe-card border border-customBeige/30 rounded-lg p-3 text-center text-pe-accent-text text-xl font-bold font-mono shadow-[0_0_15px_rgba(230,194,153,0.1)] h-[50px] flex items-center justify-center">
                                                {calcResultMarge} <span className="text-[0.8rem] ml-1 text-stone-500 dark:text-gray-500 font-sans">%</span>
                                            </div>
                                        </div>
                                      </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </aside>
        </div>
      </main>
    </div>
  );
};

export default ProduktRechnerPage;